import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

// Color palette for task categories - matches Figma design
const TASK_COLORS = {
    study: { bg: '#7C5CFC', light: '#A789FF' },      // Purple
    exercise: { bg: '#4ADE80', light: '#86EFAC' },   // Green  
    health: { bg: '#F87171', light: '#FCA5A5' },     // Red/Coral
    work: { bg: '#3B82F6', light: '#60A5FA' },       // Blue
    productivity: { bg: '#FACC15', light: '#FDE047' }, // Yellow/Amber
    learning: { bg: '#F472B6', light: '#F9A8D4' },   // Pink
    default: { bg: '#3B82F6', light: '#60A5FA' }     // Default Blue
};

// Icon mapping for categories
const CATEGORY_ICONS = {
    study: '📖',
    exercise: '🚴',
    health: '💊',
    work: '💼',
    productivity: '⚡',
    learning: '🌐',
    household: '🏠',
    wellness: '🧘',
    default: '📝'
};

const TimelineTaskCard = ({ task, onClick, onCheck, colorIndex = 0 }) => {
    // Determine color based on category or use colorIndex for variety
    const getColor = () => {
        if (task.category && TASK_COLORS[task.category]) {
            return TASK_COLORS[task.category];
        }
        // Fallback to rotating colors based on index
        const colorKeys = Object.keys(TASK_COLORS).filter(k => k !== 'default');
        return TASK_COLORS[colorKeys[colorIndex % colorKeys.length]];
    };

    const getIcon = () => {
        if (task.icon) return task.icon;
        if (task.category && CATEGORY_ICONS[task.category]) {
            return CATEGORY_ICONS[task.category];
        }
        return CATEGORY_ICONS.default;
    };

    const formatTimeRange = () => {
        // If task has specific start/end times
        if (task.startTime && task.endTime) {
            return `${task.startTime} - ${task.endTime}`;
        }
        
        // Fallback based on timeSlot
        switch(task.timeSlot) {
            case 'morning': return '09:00 AM - 10:00 AM';
            case 'day': return '11:00 AM - 12:00 PM';
            case 'evening': return '06:00 PM - 07:00 PM';
            default: return 'Anytime';
        }
    };

    const color = getColor();
    const isCompleted = task.completed;

    const handleCheck = (e) => {
        e.stopPropagation();
        onCheck && onCheck(e);
    };

    return (
        <motion.div
            layout
            onClick={onClick}
            whileHover={{ scale: 1.02, x: 4 }}
            whileTap={{ scale: 0.98 }}
            className={`relative p-4 rounded-2xl cursor-pointer overflow-hidden shadow-lg transition-all ${
                isCompleted ? 'opacity-60' : ''
            }`}
            style={{ 
                backgroundColor: color.bg,
                boxShadow: `0 8px 24px ${color.bg}30`
            }}
        >
            {/* Gradient Overlay for depth */}
            <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%, rgba(0,0,0,0.05) 100%)'
                }}
            />

            <div className="relative z-10 flex items-center gap-4">
                {/* Icon container - Clickable for quick check */}
                <motion.button 
                    whileTap={{ scale: 0.8 }}
                    onClick={handleCheck}
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0 hover:bg-white/10 transition-colors"
                    style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                >
                    {isCompleted ? (
                        <Check size={22} className="text-white" strokeWidth={3} />
                    ) : (
                        getIcon()
                    )}
                </motion.button>
                
                <div className="flex-1 min-w-0">
                    <h4 className={`font-bold text-white text-base leading-tight truncate ${
                        isCompleted ? 'line-through decoration-white/60' : ''
                    }`}>
                        {task.title}
                    </h4>
                    <p className="text-white/75 text-xs font-medium mt-1 tracking-wide">
                        {formatTimeRange()}
                    </p>
                </div>

                {/* Completed checkmark badge */}
                {isCompleted && (
                    <div className="w-6 h-6 rounded-full bg-white/30 flex items-center justify-center">
                        <Check size={14} className="text-white" strokeWidth={3} />
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default TimelineTaskCard;
export { TASK_COLORS, CATEGORY_ICONS };
